// src/components/quizzes/sfx.js
//
// One shared <audio> per sound file. The games used to write
//   const correctSound = new Audio(url);
// inside the component body, which builds (and downloads) a brand-new audio
// player on EVERY re-render — 16 times a second while a question is read
// aloud, and on every tap/tick in the bonus rounds. That is what made the
// games stutter. These helpers build each sound once and reuse it.

// PHONES (Nov 2026): starting an <audio> element is slow on a phone — each
// tap's sound held the page up for a moment, so fast tapping in the bonus
// rounds stuttered. Sounds are now also loaded once into the Web Audio API,
// where starting one costs almost nothing. Until a sound has loaded (or if
// the file can't be fetched), the old <audio> pool plays it instead, so
// nothing ever goes silent.

let ctx = null;
const buffers = new Map(); // url -> AudioBuffer | Promise | "fail"

const audioCtx = () => {
  if (ctx || typeof window === "undefined") return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    return null;
  }
  // phones only let sound start after a tap: wake the sound engine then
  const wake = () => {
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  };
  ["touchend", "pointerup", "click", "keydown"].forEach((t) => document.addEventListener(t, wake, { capture: true, passive: true }));
  return ctx;
};

/** Start loading a sound into Web Audio (safe to call often). */
export const preloadFast = (url) => {
  if (!url || buffers.has(url)) return;
  const c = audioCtx();
  if (!c || typeof fetch === "undefined") {
    buffers.set(url, "fail");
    return;
  }
  const p = fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.arrayBuffer();
    })
    .then((data) => new Promise((res, rej) => c.decodeAudioData(data, res, rej)))
    .then((buf) => {
      buffers.set(url, buf);
      return buf;
    })
    .catch(() => {
      buffers.set(url, "fail");
    });
  buffers.set(url, p);
};

/**
 * Play a sound through Web Audio. Returns true if it played, false if the
 * caller should use its <audio> fallback (not loaded yet, or no Web Audio).
 */
export const playFast = (url, volume = 1) => {
  const c = audioCtx();
  const buf = buffers.get(url);
  if (!buf) preloadFast(url);
  if (!c || !buf || buf === "fail" || typeof buf.then === "function") return false;
  if (c.state === "suspended") c.resume().catch(() => {});
  if (c.state !== "running") return false;
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = Math.max(0, Math.min(1, Number(volume)));
    src.connect(g);
    g.connect(c.destination);
    src.start(0);
    return true;
  } catch {
    return false;
  }
};

const cache = new Map();

export const sfx = (url) => {
  if (typeof Audio === "undefined") return { play: () => Promise.resolve(), pause() {}, currentTime: 0 };
  if (!cache.has(url)) {
    const a = new Audio(url);
    a.preload = "auto";
    cache.set(url, a);
  }
  return cache.get(url);
};

/** Round-robin pool so rapid taps overlap instead of cutting each other off. Built once per url+size. */
const pools = new Map();
export const sfxPool = (url, size = 5) => {
  const key = `${size}|${url}`;
  if (pools.has(key)) return pools.get(key);
  const list = typeof Audio === "undefined" ? [] : Array.from({ length: size }, () => {
    const a = new Audio(url);
    a.preload = "auto";
    return a;
  });
  let i = 0;
  preloadFast(url);
  const play = () => {
    if (playFast(url)) return;
    const a = list[i];
    if (!a) return;
    i = (i + 1) % size;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch {
      /* autoplay policy */
    }
  };
  pools.set(key, play);
  return play;
};
